import { baseApi, API_TAGS, createCacheTags, createEndpointBuilder, type Params, BaseQueryParams } from '@/shared/api';
import { handleFileDownloadResponse } from '@/shared/lib';
import {
  type ServiceRequestInfiniteQueryArgs,
  type ServiceRequestsPageResponse,
  type ServiceRequestsSummaryResponse,
  type ServiceRequestExportParams,
  type ServiceRequestListParams,
  type ServiceRequestRejectRequest,
  type ServiceRequestMoveToConsignmentRequest,
  type ServiceRequestLinkConsignmentRequest,
  type ServiceRequestActionResult,
  type ServiceRequestDetail,
  type ServiceRequestDetailParams
} from '../model/types';

const serviceRequestsEndpoint = createEndpointBuilder('/api/v2/service-requests');

export const serviceRequestApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getServiceRequests: builder.infiniteQuery<ServiceRequestsPageResponse, ServiceRequestInfiniteQueryArgs, number>({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          const nextPage = lastPageParam + 1;
          if (nextPage >= lastPage.totalPages) {
            return undefined;
          }
          return nextPage;
        }
      },
      query: ({ queryArg, pageParam }) => ({
        url: serviceRequestsEndpoint(''),
        params: {
          ...queryArg,
          page: pageParam
        }
      }),
      providesTags: (_result, _error, { queryArg }) =>
        createCacheTags({
          baseTag: API_TAGS.SERVICE_REQUESTS,
          params: queryArg as unknown as Params
        })
    }),

    getServiceRequestsSummary: builder.query<
      ServiceRequestsSummaryResponse,
      Pick<ServiceRequestListParams, 'projectId' | 'siteId' | 'search'>
    >({
      query: (params) => ({
        url: serviceRequestsEndpoint('/summary'),
        params
      }),
      providesTags: [{ type: API_TAGS.SERVICE_REQUESTS, id: 'SUMMARY' }]
    }),

    exportServiceRequests: builder.query<Blob, ServiceRequestExportParams>({
      query: (params) => ({
        url: serviceRequestsEndpoint('/export'),
        params,
        responseHandler: (response) =>
          handleFileDownloadResponse(response, `service-requests.${params.format || 'xlsx'}`),
        cache: 'no-cache'
      })
    }),

    rejectServiceRequest: builder.mutation<ServiceRequestActionResult, ServiceRequestRejectRequest>({
      query: ({ id, projectId, siteId, rejectComment, rejectReason }) => ({
        url: serviceRequestsEndpoint(`/${id}/reject`),
        method: 'POST',
        params: { projectId, siteId },
        body: { rejectComment, rejectReason }
      }),
      invalidatesTags: [{ type: API_TAGS.SERVICE_REQUESTS }]
    }),

    moveServiceRequestToConsignment: builder.mutation<
      ServiceRequestActionResult,
      ServiceRequestMoveToConsignmentRequest
    >({
      query: ({ id, projectId, siteId, remarks }) => ({
        url: serviceRequestsEndpoint(`/${id}/move-to-consignment`),
        method: 'POST',
        params: { projectId, siteId },
        body: { remarks, siteId }
      }),
      invalidatesTags: [{ type: API_TAGS.SERVICE_REQUESTS }]
    }),

    linkServiceRequestToConsignment: builder.mutation<ServiceRequestActionResult, ServiceRequestLinkConsignmentRequest>(
      {
        query: ({ id, projectId, siteId, consignmentId }) => ({
          url: serviceRequestsEndpoint(`/${id}/link-consignment`),
          method: 'POST',
          params: { projectId, siteId, consignmentId }
        }),
        invalidatesTags: [{ type: API_TAGS.SERVICE_REQUESTS }]
      }
    ),

    getServiceRequestDetail: builder.query<ServiceRequestDetail, ServiceRequestDetailParams>({
      query: ({ id, projectId, siteId }) => ({
        url: serviceRequestsEndpoint(`/${id}`),
        params: { projectId, siteId }
      }),
      providesTags: (_result, _error, { id }) => [{ type: API_TAGS.SERVICE_REQUESTS, id }]
    }),

    printServiceRequestForm: builder.query<Blob, BaseQueryParams & { id: number; lang?: string }>({
      query: ({ id, projectId, siteId, lang = 'en' }) => ({
        url: serviceRequestsEndpoint(`/${id}/print-form`),
        params: { projectId, siteId, lang },
        responseHandler: (response) => handleFileDownloadResponse(response, `service-request-${id}.pdf`),
        cache: 'no-cache'
      })
    })
  })
});

export const {
  useGetServiceRequestsInfiniteQuery,
  useGetServiceRequestsSummaryQuery,
  useLazyExportServiceRequestsQuery,
  useRejectServiceRequestMutation,
  useMoveServiceRequestToConsignmentMutation,
  useLinkServiceRequestToConsignmentMutation,
  useGetServiceRequestDetailQuery,
  useLazyPrintServiceRequestFormQuery
} = serviceRequestApi;
